/* eslint-disable import/no-cycle */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { Subject, map, merge, of } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  FlxAccordionComponent,
  FlxAccordionItemComponent,
  FlxCardComponent,
} from '@flowx/angular-ui-toolkit'
import {
  CardDefaultCollapseState,
  CardTemplateConfig,
  ComponentIdentifierEnum,
  getCurrentReadOnlyState,
  isCardDisabled$,
  isCurrentNode$,
  isUserTaskRoot,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { emitScreenAnalyticsOnDisplay } from '../../utils/screen-analytics.utils'
import { getBoolExpressionSignal, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-card-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxAccordionComponent,
    FlxAccordionItemComponent,
    FlxCardComponent,
    FlxTreeNodeComponent,
    FlxLocalizePipe,
    FlxThemeModule,
    FlxStyleModule,
  ],
  templateUrl: './flx-card-wrapper.component.html',
  styleUrl: './flx-card-wrapper.component.scss',
  host: {
    '[class]': `displayOptions().className`,
    '[attr.id]': 'templateConfig().linkId',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxCardWrapperComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #destroyRef = inject(DestroyRef)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #keepCardOpened = false

  templateConfig = input.required<CardTemplateConfig & { canGoBack: boolean; linkId?: string }>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  isRoot = computed(() => isUserTaskRoot(this.templateConfig()))
  defaultOpen = computed(() =>
    this.isRoot()
      ? true
      : this.flowxProps().defaultCollapseState !== CardDefaultCollapseState.COLLAPSED
  )

  openState$ = new Subject<boolean>()
  isOpened: Signal<boolean>
  isCurrent: Signal<boolean>

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  // expressions
  isDisabled: Signal<boolean>
  isHidden: Signal<boolean>

  // dynamic props
  title: Signal<string>
  subtitle: Signal<string>

  readonly ComponentIdentifierEnum = ComponentIdentifierEnum

  readonly = getCurrentReadOnlyState

  ngOnInit(): void {
    emitScreenAnalyticsOnDisplay(this.templateConfig(), this.flowxProps(), this.#destroyRef)

    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const { title, subtitle, hide } = this.flowxProps()
      this.title = getDynamicPropSignal(title, this.templateConfig())
      this.subtitle = getDynamicPropSignal(subtitle, this.templateConfig())
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })

      const { processInstanceUuid, nodeDefinitionId, context } = this.templateConfig()
      const defaultOpen = this.defaultOpen()
      const hasDefaultCollapseState =
        !this.isRoot() && this.flowxProps().defaultCollapseState !== null
      this.isCurrent = toSignal(isCurrentNode$(processInstanceUuid, nodeDefinitionId, context))
      const openState$ = hasDefaultCollapseState
        ? this.openState$
        : merge(
            isCurrentNode$(processInstanceUuid, nodeDefinitionId, context).pipe(
              map((isCurrent) => isCurrent || (!isCurrent && this.#keepCardOpened))
            ),
            this.openState$
          )
      this.isOpened = toSignal(openState$, { initialValue: defaultOpen })
      this.isDisabled = toSignal(
        this.readonly() ? of(false) : isCardDisabled$(processInstanceUuid, this.templateConfig())
      )

      const { conditionals } = this.style()
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  toggleOpenState(isOpened: boolean): void {
    if (this.templateConfig().canGoBack && !this.isCurrent()) {
      this.#keepCardOpened = isOpened
    }
    this.openState$.next(isOpened)
  }
}
